import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import {
  Box,
  Button,
  Theme,
  Typography,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormGroup,
  FormHelperText,
  Grid,
  Dialog,
  DialogContent
} from '@material-ui/core';
import { ThemeProvider, createStyles, makeStyles, useTheme } from '@material-ui/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import DOMPurify from 'dompurify';

import {
  GET_SIGNUP_INFO,
  GET_SIGNUP_INFO_OPTION,
  UPSERT_SIGNUP_INFO_M
} from 'src/apollo/api';
import { MainTheme } from 'src/theme';

import { useUserInfo } from '../../../services';
import { signupInfoBanner } from '../../assets';

const FIELDS = {
  phone: 'phone',
  role: 'role',
  otherRole: 'otherRole',
  industry: 'industry',
  otherIndustry: 'otherIndustry',
  companySize: 'companySize',
  region: 'region',
  subRegion: 'subRegion',
  from: 'from',
  otherFrom: 'otherFrom'
};
const OTHER_VALUE = 'Other';
const US_VALUE = 'United States';
const PRIORITY_COUNTRYS = ["United States", "Canada", "United Kingdom", "Mexico"];

const useStyles = makeStyles<Theme>(theme => {
  return createStyles({
    dialogPaper: {
      maxWidth: '871px',
    },
    leftGrid: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '24px',
      backgroundColor: 'white',
    },
    rightGrid: {
      backgroundImage: `url('${signupInfoBanner}')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    },
    submitButton: {
      marginTop: '24px',
      padding: '8px 22px',
      textTransform: 'none'
    },
    skipButton: {
      position: "absolute",
      bottom: "24px",
      right: "24px",
      borderRadius: '4px',
      outline: 'solid #ffffff',
      background: 'rgba(255, 255, 255, 0.00)',
      color: '#ffffff',
      padding: '8px 22px',
      textTransform: 'none'
    },
    title: {
      fontSize: '18px',
      paddingBottom: '16px',
    }
  });
});

const selectMenuProps ={
  anchorOrigin: {
    vertical: 'bottom' as 'top' | 'bottom' | 'center',
    horizontal: 'left' as 'left' | 'right' | 'center',
  },
  transformOrigin: {
    vertical: 'top' as 'top' | 'bottom' | 'center',
    horizontal: 'left' as 'left' | 'right' | 'center',
  },
  getContentAnchorEl: null, // Prevents incorrect positioning
};

const SignupInfoForm: React.FC<{
  onClose?: () => void;
  onSkip?: () => Promise<void>;
  isEditingMode?: boolean;
  canSkip?: boolean;
}> = ({ onClose, onSkip, isEditingMode, canSkip }) => {
  const {
    me: {
      id: userId,
    }
  } = useUserInfo();

  const theme = useTheme();
  const classes = useStyles(theme);

  const [roleMenuItemValues, setRoleMenuItemValues] = useState<{ roleId: number; role: string }[]>([]);
  const [industryMenuItemValues, setindustryMenuItemValues] = useState<{ industryId: number; industry: string }[]>([]);
  const [companySizeMenuItemValues, setCompanySizeMenuItemValues] = useState<{ companySizeId: number; companySize: string }[]>([]);
  const [regionMenuItemValues, setRegionMenuItemValues] = useState<{ regionId: number; region: string }[]>([]);
  const [subRegionMenuItemValues, setSubRegionMenuItemValues] = useState<{ subRegionId: number; regionId: number; subRegion: string }[]>([]);
  const [fromMenuItemValues, setFromMenuItemValues] = useState<{ fromId: number; from: string }[]>([]);

  const [formData, setFormData] = useState({
    [FIELDS.phone]: '',
    [FIELDS.role]: '',
    [FIELDS.otherRole]: '',
    [FIELDS.industry]: '',
    [FIELDS.otherIndustry]: '',
    [FIELDS.companySize]: '',
    [FIELDS.region]: '',
    [FIELDS.subRegion]: '',
    [FIELDS.from]: '',
    [FIELDS.otherFrom]: ''
  });
  const [fieldErrors, setFieldErrors] = useState({
    [FIELDS.phone]: false,
    [FIELDS.role]: false,
    [FIELDS.otherRole]: false,
    [FIELDS.industry]: false,
    [FIELDS.otherIndustry]: false,
    [FIELDS.region]: false,
    [FIELDS.subRegion]: false,
    [FIELDS.otherFrom]: false
  });
  const [shoudlShowOtherRole, setShoudlShowOtherRole] = useState<boolean>(false);
  const [shoudlShowOtherIndustry, setShoudlShowOtherIndustry] = useState<boolean>(false);
  const [shoudlShowOtherFrom, setShoudlShowOtherFrom] = useState<boolean>(false);
  const [shoudlShowSubRegion, setShoudlShowSubRegion] = useState<boolean>(false);

  const handleFormFieldChange = useCallback((field: string, isPhoneFormat: boolean = false) => {
    return (event) => {
      event.persist();

      const value = event.target.value;
      const otherValue = OTHER_VALUE?.toLocaleLowerCase();
      const usValue = US_VALUE?.toLocaleLowerCase();

      const phoneRegex = /^[0-9+\-()]*$/;

      if (!phoneRegex.test(value) && isPhoneFormat) {
        return;
      }

      setFormData((formData) => ({
        ...formData,
        [field]: value,
      }));

      switch (field) {
        case FIELDS.role: {
          const roleValue = event.currentTarget?.dataset.role?.toLowerCase();

          if (roleValue === otherValue) {
            setShoudlShowOtherRole(true);
          } else {
            setShoudlShowOtherRole(false);
          }
          break;
        }
        case FIELDS.industry: {
          const industryValue = event.currentTarget?.dataset.industry?.toLowerCase();

          if (industryValue === otherValue) {
            setShoudlShowOtherIndustry(true);
          } else {
            setShoudlShowOtherIndustry(false);
          }
          break;
        }
        case FIELDS.from: {
          const fromValue = event.currentTarget?.dataset.from?.toLowerCase();

          if (fromValue === otherValue) {
            setShoudlShowOtherFrom(true);
          } else {
            setShoudlShowOtherFrom(false);
          }
          break;
        }
        case FIELDS.region: {
          const regionValue = event.currentTarget?.dataset.region?.toLowerCase();

          if (regionValue === usValue) {
            setFormData((formData) => ({
              ...formData,
              [FIELDS.subRegion]: '',
            }));
            setShoudlShowSubRegion(true);
          } else {
            setShoudlShowSubRegion(false);
          }
          break;
        }
      }

      if (fieldErrors[field]) {
        setFieldErrors((fieldErrors) => ({
          ...fieldErrors,
          [field]: false,
        }));
      }
    };
  }, [setFormData, formData, setShoudlShowOtherRole, setShoudlShowOtherIndustry, setShoudlShowOtherFrom, setShoudlShowSubRegion, fieldErrors, setFieldErrors]);

  const validateForm = useCallback(() => {
    let nextFieldErrors = {
      [FIELDS.phone]: !formData[FIELDS.phone],
      [FIELDS.role]: !formData[FIELDS.role],
      [FIELDS.industry]: !formData[FIELDS.industry],
      [FIELDS.region]: !formData[FIELDS.region]
    };

    if (shoudlShowOtherRole) {
      nextFieldErrors[FIELDS.otherRole] = !formData[FIELDS.otherRole];
    }

    if (shoudlShowOtherIndustry) {
      nextFieldErrors[FIELDS.otherIndustry] = !formData[FIELDS.otherIndustry];
    }

    if (shoudlShowSubRegion) {
      nextFieldErrors[FIELDS.subRegion] = !formData[FIELDS.subRegion];
    }

    if (shoudlShowOtherFrom) {
      nextFieldErrors[FIELDS.otherFrom] = !formData[FIELDS.otherFrom];
    }

    setFieldErrors(nextFieldErrors);

    return !Object.values(nextFieldErrors).some((error) => error);
  }, [formData, setFieldErrors, shoudlShowOtherFrom, shoudlShowOtherIndustry, shoudlShowOtherRole, shoudlShowSubRegion]);

  const [upsertSignupInfo] = useMutation(UPSERT_SIGNUP_INFO_M, {
    onCompleted: (data) => {
      onClose && onClose();
    }
  });

  const { loading: isSignupInfoLoading, error: fetchSignupInfoError, data: signupInfoData } = useQuery(
    GET_SIGNUP_INFO, {
      fetchPolicy: 'cache-and-network',
      variables: { userId },
      skip: !userId || !isEditingMode,
      onCompleted(data) {
        if (data?.getSignupInfo) {
          const {
            companySizeId,
            fromId,
            industryId,
            otherFrom,
            otherIndustry,
            otherRole,
            phone,
            regionId,
            roleId,
            subRegionId,
            hasSkip,
          } = data.getSignupInfo;

          if (hasSkip) {
            // emtpy the form because the user deos not fill before.
            return;
          } else {
            let nextFormData = {
              [FIELDS.phone]: phone ? phone : '',
              [FIELDS.role]: roleId ? roleId : '',
              [FIELDS.industry]: industryId ? industryId : '',
              [FIELDS.region]: regionId ? regionId : '',
            };

            let nextFieldErrors = {
              [FIELDS.phone]: false,
              [FIELDS.role]: false,
              [FIELDS.otherRole]: false,
              [FIELDS.industry]: false,
              [FIELDS.otherIndustry]: false,
              [FIELDS.region]: false,
              [FIELDS.subRegion]: false,
              [FIELDS.otherFrom]: false,
            }

            if (otherRole) {
              nextFormData[FIELDS.otherRole] = otherRole;
              setShoudlShowOtherRole(true);
            }

            if (otherIndustry) {
              nextFormData[FIELDS.otherIndustry] = otherIndustry;
              setShoudlShowOtherIndustry(true);
            }

            if (otherFrom) {
              nextFormData[FIELDS.otherFrom] = otherFrom;
              setShoudlShowOtherFrom(true);
            }

            if (subRegionId) {
              nextFormData[FIELDS.subRegion] = subRegionId;
              setShoudlShowSubRegion(true);
            }

            if (companySizeId) {
              nextFormData[FIELDS.companySize] = companySizeId;
            }

            if (fromId) {
              nextFormData[FIELDS.from] = fromId;
              setShoudlShowSubRegion(true);
            }

            setFormData((formData) => ({
              ...formData,
              ...nextFormData
            }));
            setFieldErrors((fieldErrors) => ({
              ...fieldErrors,
              ...nextFieldErrors
            }));
          }
        }
      }
    });

  const { loading: isSignupInfoOptionLoading, error: fetchSignupInfoOptionError, data: signupInfoOptionData } = useQuery(
    GET_SIGNUP_INFO_OPTION, {
      fetchPolicy: 'cache-and-network',
      onCompleted(data) {
        const roleOptions = data?.getSignupInfoOption?.roleOptions;
        const industryOptions = data?.getSignupInfoOption?.industryOptions;
        const companySizeOptions = data?.getSignupInfoOption?.companySizeOptions;
        const regionOptions = data?.getSignupInfoOption?.regionOptions;
        const subRegionOptions = data?.getSignupInfoOption?.subRegionOptions;
        const fromOptions = data?.getSignupInfoOption?.fromOptions;

        const sortedRegionOptions = regionOptions ? regionOptions.sort((a, b) => {
          const { region: regionA } = a;
          const { region: regionB } = b;
          const regionAIndex = PRIORITY_COUNTRYS.indexOf(regionA);
          const regionBIndex = PRIORITY_COUNTRYS.indexOf(regionB);

          if (regionAIndex !== -1 && regionBIndex !== -1) {
            return regionAIndex - regionBIndex;
          } else if (regionAIndex !== -1) {
            return -1;
          } else if (regionBIndex !== -1) {
            return 1;
          } else {
            return regionA.localeCompare(regionB);
          }
        }) : regionOptions;

        setRoleMenuItemValues(roleOptions);
        setindustryMenuItemValues(industryOptions);
        setCompanySizeMenuItemValues(companySizeOptions);
        setRegionMenuItemValues(sortedRegionOptions);
        setSubRegionMenuItemValues(subRegionOptions);
        setFromMenuItemValues(fromOptions);
      }
    });

  const handleClickSend = useCallback(async () => {
    if (validateForm()) {
      await upsertSignupInfo({
        variables: {
          userId,
          phone: formData[FIELDS.phone] === '' ? null : DOMPurify.sanitize(formData[FIELDS.phone]),
          roleId: formData[FIELDS.role] === '' ? null : formData[FIELDS.role],
          industryId: formData[FIELDS.industry] === '' ? null : formData[FIELDS.industry],
          companySizeId: formData[FIELDS.companySize] === '' ? null : formData[FIELDS.companySize],
          regionId: formData[FIELDS.region] === '' ? null : formData[FIELDS.region],
          subRegionId: formData[FIELDS.subRegion] === '' ? null : formData[FIELDS.subRegion],
          fromId: formData[FIELDS.from] === '' ? null : formData[FIELDS.from],
          otherRole: formData[FIELDS.otherRole] === '' || !shoudlShowOtherRole ? null : DOMPurify.sanitize(formData[FIELDS.otherRole]),
          otherFrom: formData[FIELDS.otherFrom] === '' || !shoudlShowOtherFrom ? null : DOMPurify.sanitize(formData[FIELDS.otherFrom]),
          otherIndustry: formData[FIELDS.otherIndustry] === '' || !shoudlShowOtherIndustry ? null : DOMPurify.sanitize(formData[FIELDS.otherIndustry]),
          hasSkip: false, // To set user has filled the info form
        }
      });
    }
  }, [upsertSignupInfo, formData, shoudlShowOtherRole, shoudlShowOtherFrom, shoudlShowOtherIndustry, validateForm]);

  const handleSkip = useCallback(async (event) => {
    onSkip && await onSkip();
    onClose && onClose();
  }, [onSkip, onClose]);

  const handleDismissDialog = useCallback(() => {
    if (isEditingMode) {
      onClose && onClose();
    }
  }, [isEditingMode, onClose]);

  const roleMenuItems = useMemo(() => {
    return roleMenuItemValues.map(({ roleId, role }: { roleId: number; role: string }, index: number) => {
      return (
        <MenuItem key={`roleMenuItems${index}`} value={roleId} data-role={role}>{role}</MenuItem>
      );
    });
  }, [roleMenuItemValues]);

  const industryMenuItems = useMemo(() => {
    return industryMenuItemValues.map(({ industryId, industry }: { industryId: number; industry: string }, index: number) => {
      return (
        <MenuItem key={`industryMenuItems${index}`} value={industryId} data-industry={industry}>{industry}</MenuItem>
      );
    });
  }, [industryMenuItemValues]);

  const companySizeMenuItems = useMemo(() => {
    return companySizeMenuItemValues.map(({ companySizeId, companySize }: { companySizeId: number; companySize: string }, index: number) => {
      return (
        <MenuItem key={`companySizeMenuItems${index}`} value={companySizeId}>{companySize}</MenuItem>
      );
    });
  }, [companySizeMenuItemValues]);

  const regionMenuItems = useMemo(() => {
    return regionMenuItemValues.map(({ regionId, region }: { regionId: number; region: string }, index: number) => {
      return (
        <MenuItem key={`regionMenuItems${index}`} value={regionId} data-region={region}>{region}</MenuItem>
      );
    });
  }, [regionMenuItemValues]);

  const subRegionMenuItems = useMemo(() => {
    return subRegionMenuItemValues.map(({ subRegionId, subRegion }: { subRegionId: number; subRegion: string }, index: number) => {
      return (
        <MenuItem key={`subRegionMenuItems${index}`} value={subRegionId}>{subRegion}</MenuItem>
      );
    });
  }, [subRegionMenuItemValues]);

  const fromMenuItems = useMemo(() => {
    return fromMenuItemValues.map(({ fromId, from }: { fromId: number; from: string }, index: number) => {
      return (
        <MenuItem key={`fromMenuItems${index}`} value={fromId} data-from={from}>{from}</MenuItem>
      );
    });
  }, [fromMenuItemValues]);

  if (isSignupInfoOptionLoading || isSignupInfoLoading) {
    return <></>;
  } else {
    return (
      <ThemeProvider theme={MainTheme}>
        <Dialog
          open
          fullWidth
          maxWidth="md"
          onClose={handleDismissDialog}
          classes={{ paper: classes.dialogPaper }}
        >
          <DialogContent style={{ padding: 0 }}>
            <Grid container>
              <Grid
                item
                xs={8}
                md={5}
                className={classes.leftGrid}
              >
                <Typography variant="h6" gutterBottom className={classes.title}>
                  Help us tailor to your business needs
                </Typography>
                <FormGroup>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={fieldErrors[FIELDS.phone]}
                      >
                        <TextField
                          label="Phone"
                          fullWidth
                          variant="outlined"
                          value={formData[FIELDS.phone]}
                          onChange={handleFormFieldChange(FIELDS.phone, true)}
                          error={fieldErrors[FIELDS.phone]}
                          helperText={fieldErrors[FIELDS.phone] ? 'Required' : ''}
                          size="small"
                          inputProps={{ maxLength: 20 }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={fieldErrors[FIELDS.role]}
                        variant="outlined"
                        size="small"
                      >
                        <InputLabel>Role</InputLabel>
                        <Select
                          value={formData[FIELDS.role]}
                          onChange={handleFormFieldChange(FIELDS.role)}
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={selectMenuProps}
                          label="Role"
                        >
                          {roleMenuItems}
                        </Select>
                        {fieldErrors[FIELDS.role] && <FormHelperText>Required</FormHelperText>}
                      </FormControl>
                      {shoudlShowOtherRole ? (
                        <Box>
                          <Box style={{height: 6}} />
                          <TextField
                            label="Role"
                            fullWidth
                            variant="outlined"
                            value={formData[FIELDS.otherRole]}
                            onChange={handleFormFieldChange(FIELDS.otherRole)}
                            error={fieldErrors[FIELDS.otherRole]}
                            helperText={fieldErrors[FIELDS.otherRole] ? 'Required' : ''}
                            size="small"
                            inputProps={{ maxLength: 50 }}
                          />
                        </Box>
                      ) : null}
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={fieldErrors[FIELDS.industry]}
                        variant="outlined"
                        size="small"
                      >
                        <InputLabel>Industry</InputLabel>
                        <Select
                          value={formData[FIELDS.industry]}
                          onChange={handleFormFieldChange(FIELDS.industry)}
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={selectMenuProps}
                          label="Industry"
                        >
                          {industryMenuItems}
                        </Select>
                        {fieldErrors[FIELDS.industry] && <FormHelperText>Required</FormHelperText>}
                      </FormControl>
                      {shoudlShowOtherIndustry ? (
                        <Box>
                          <Box style={{height: 6}} />
                          <TextField
                            label="Industry"
                            fullWidth
                            variant="outlined"
                            value={formData[FIELDS.otherIndustry]}
                            onChange={handleFormFieldChange(FIELDS.otherIndustry)}
                            error={fieldErrors[FIELDS.otherIndustry]}
                            helperText={fieldErrors[FIELDS.otherIndustry] ? 'Required' : ''}
                            size="small"
                            inputProps={{ maxLength: 50 }}
                          />
                        </Box>
                      ) : null}
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        size="small"
                      >
                        <InputLabel>Company size (optional)</InputLabel>
                        <Select
                          value={formData[FIELDS.companySize]}
                          onChange={handleFormFieldChange(FIELDS.companySize)}
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={selectMenuProps}
                          label="Company size (optional)"
                        >
                          {companySizeMenuItems}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        error={fieldErrors[FIELDS.region]}
                        size="small"
                      >
                        <InputLabel>Region</InputLabel>
                        <Select
                          value={formData[FIELDS.region]}
                          onChange={handleFormFieldChange(FIELDS.region)}
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={selectMenuProps}
                          label="Region"
                        >
                          {regionMenuItems}
                        </Select>
                        {fieldErrors[FIELDS.region] && <FormHelperText>Required</FormHelperText>}
                      </FormControl>
                      {shoudlShowSubRegion ? (
                        <Box>
                          <Box style={{height: 6}} />
                          <FormControl
                            fullWidth
                            variant="outlined"
                            error={fieldErrors[FIELDS.subRegion]}
                            size="small"
                          >
                            <InputLabel>State</InputLabel>
                            <Select
                              value={formData[FIELDS.subRegion]}
                              onChange={handleFormFieldChange(FIELDS.subRegion)}
                              IconComponent={KeyboardArrowDownIcon}
                              MenuProps={selectMenuProps}
                              label="State"
                            >
                              {subRegionMenuItems}
                            </Select>
                            {fieldErrors[FIELDS.subRegion] && <FormHelperText>Required</FormHelperText>}
                          </FormControl>
                        </Box>
                      ) : null}
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        size="small"
                      >
                        <InputLabel>Where did you find us? (optional)</InputLabel>
                        <Select
                          value={formData.from}
                          onChange={handleFormFieldChange(FIELDS.from)}
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={selectMenuProps}
                          label="Where did you find us? (optional)"
                        >
                          {fromMenuItems}
                        </Select>
                      </FormControl>
                      {shoudlShowOtherFrom ? (
                        <Box>
                          <Box style={{height: 6}} />
                          <TextField
                            label="Where"
                            fullWidth
                            variant="outlined"
                            value={formData[FIELDS.otherFrom]}
                            onChange={handleFormFieldChange(FIELDS.otherFrom)}
                            error={fieldErrors[FIELDS.otherFrom]}
                            helperText={fieldErrors[FIELDS.otherFrom] ? 'Required' : ''}
                            size="small"
                            inputProps={{ maxLength: 50 }}
                          />
                        </Box>
                      ) : null}
                    </Grid>
                  </Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClickSend}
                    className={classes.submitButton}
                  >
                    Send
                  </Button>
                </FormGroup>
                </Grid>
              <Grid
                item
                xs={4}
                md={7}
                className={classes.rightGrid}
              >
                {canSkip || isEditingMode ? (
                  <Button
                    variant="text"
                    onClick={handleSkip}
                    className={classes.skipButton}
                  >
                    Skip
                  </Button>
                ) : null}
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
      </ThemeProvider>
    );
  }
};

export default SignupInfoForm;